import { useEffect, useRef } from 'react'
import { type AppLocale, toTurnstileLanguage } from '@kcm/contracts'

/**
 * Cloudflare Turnstile (PLAN.md §18.2).
 *
 * On by default. The site key comes from the site config, so a white-label
 * domain uses its own widget and nothing about it is baked into the bundle.
 *
 * The token is single-use and short-lived: Cloudflare rejects a replay, so it
 * must be re-issued after every submit rather than held and reused. Callers
 * reset the widget via the `resetKey` prop after a failed attempt.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      remove: (id: string) => void
    }
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/** Shared across mounts — loading it twice re-registers the global. */
let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      // Let a later mount try again: this is usually a blocked request or a
      // dead connection rather than a permanent failure.
      scriptPromise = null
      reject(new Error('turnstile script failed to load'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

interface TurnstileProps {
  siteKey: string
  onToken: (token: string) => void
  /**
   * The challenge cannot produce a token here at all — a blocked script, or a
   * key not registered for this hostname. Distinct from a failed attempt,
   * which resolves itself on a retry. The caller needs it because a widget
   * that will never issue a token must not silently disable the form.
   */
  onUnavailable?: () => void
  /** Change this to discard the spent token and issue a fresh one. */
  resetKey?: number
  locale?: AppLocale
}

export function Turnstile({
  siteKey,
  onToken,
  onUnavailable,
  resetKey = 0,
  locale,
}: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null)
  // Held in a ref so re-rendering with a new callback does not tear down and
  // re-render the widget, which would invalidate a token already issued.
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken
  const onUnavailableRef = useRef(onUnavailable)
  onUnavailableRef.current = onUnavailable

  useEffect(() => {
    let widgetId: string | undefined
    let cancelled = false

    void loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return
        widgetId = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          ...(locale ? { language: toTurnstileLanguage(locale) } : {}),
          callback: (token: string) => onTokenRef.current(token),
          // An expired or errored challenge must clear the token we are holding,
          // or the form would submit one the server is certain to reject.
          'expired-callback': () => onTokenRef.current(''),
          'error-callback': () => {
            onTokenRef.current('')
            onUnavailableRef.current?.()
          },
        })
      })
      .catch(() => {
        // §18.2: the challenge failing to load must not be a dead end — the
        // server decides whether a missing token is fatal. Reported rather
        // than swallowed, so the form can stay submittable and say why.
        onUnavailableRef.current?.()
      })

    return () => {
      cancelled = true
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    }
  }, [siteKey, resetKey, locale])

  return <div ref={ref} className="min-h-[65px]" />
}
